import moment from "moment";
import DateFilter from "components/input/CalendarInput.component";
import {NewCalendar} from "components/common/calendar/Calendar.component";
import {launchDarkly} from "utils/launchDarkly";
import {formatDate, formatDocumentOrganizationUnit} from "utils/valueFormatter.function";
import type {DocumentDetailsProps} from "types/documentData";
import {DocumentDataField, DocumentDataGrid} from "./DocumentDataField.component";
import {DocumentAmountField} from "./DocumentAmount.component";
import {PreApprovalDetails} from "./PreApprovalDetails.component";

const INVOICE_DATE = "invoiceDate";
const DUE_DATE = "dueDate";
type EditableDate = typeof INVOICE_DATE | typeof DUE_DATE;

const WorkflowDocumentDetailsDTO = ({details, warnings, translate, saveData, userLanguage, dateFormat, readOnly}: DocumentDetailsProps) => {
    const tzOffset = new Date().getTimezoneOffset() * 60 * 1000;
    const editableFields = !readOnly && details.editableFields ? details.editableFields : [];
    const invoiceDateIsEditable = editableFields.includes(INVOICE_DATE);
    const dueDateIsEditable = editableFields.includes(DUE_DATE);
    const useNewCalendar = launchDarkly.userClient?.variation("fe-use-new-calendar", false) ?? false;

    // the picked day is saved at midnight UTC, the hour that matches the local timezone offset
    const dateChanged = (type: EditableDate, value: unknown) => {
        if (!(value instanceof Date) || isNaN(value.valueOf())) {
            return;
        }
        const hours = Math.abs(tzOffset / (60 * 60 * 1000));
        const date = moment(value).set({hour: hours, minute: 0, second: 0, millisecond: 0});
        saveData({...details, [type]: date.toDate()} as typeof details);
    };

    const toIsoDate = (value?: number | null) =>
        value ? moment.utc(value - tzOffset).toISOString(false).substring(0, 10) : undefined;

    // Both pickers keep the same testid so the surrounding markup and tests stay stable.
    // The new calendar reports a range, of which only the start date is a single-day pick.
    const renderDatePicker = (type: EditableDate, isoDate: string | undefined, testid: string) => {
        if (useNewCalendar) {
            // The date is only read when the calendar mounts, so remount it when the saved
            // value changes to keep the input in sync with the document details.
            const selectedDate = moment(isoDate).toDate();
            return (
                <NewCalendar key={`${type}-${isoDate}`}
                             id={testid}
                             singlePanel
                             translate={translate}
                             initialStartDate={selectedDate}
                             initialEndDate={selectedDate}
                             fieldClassName="w-100"
                             onChange={(range) => dateChanged(type, range.startDate)}/>
            );
        }

        return (
            <DateFilter style="popup"
                        testid={testid}
                        translate={translate}
                        userLanguage={userLanguage}
                        format={dateFormat}
                        selectedValue={isoDate}
                        onChange={(value: unknown) => dateChanged(type, value)}/>
        );
    };

    return (
        <DocumentDataGrid>
            {details.description &&
                <DocumentDataField id="description" wide label={translate("documentData.description")}
                                   value={details.description} warnings={warnings.description}/>}
            {details.supplierName &&
                <DocumentDataField id="supplierName" label={translate("documentData.supplierName")}
                                   value={details.supplierName} warnings={warnings.supplierName}/>}
            {details.supplierNumber &&
                <DocumentDataField id="supplierNumber" label={translate("documentData.supplierNumber")}
                                   value={details.supplierNumber} warnings={warnings.supplierNumber}/>}
            {details.invoiceNumber &&
                <DocumentDataField id="invoiceNumber" label={translate("documentData.invoiceNumber")}
                                   value={details.invoiceNumber} warnings={warnings.invoiceNumber}/>}
            {details.documentId &&
                <DocumentDataField id="documentId" label={translate("documentData.documentID")}
                                   value={details.documentId} warnings={warnings.documentId}/>}
            {!!details.invoiceDate && (invoiceDateIsEditable ?
                <DocumentDataField id="invoiceDate" label={translate("documentData.invoiceDate")} warnings={warnings.invoiceDate}>
                    {renderDatePicker(INVOICE_DATE, toIsoDate(details.invoiceDate), "invoiceDate-datepicker")}
                </DocumentDataField> :
                <DocumentDataField id="invoiceDate" label={translate("documentData.invoiceDate")}
                                   value={formatDate(details.invoiceDate, null)} warnings={warnings.invoiceDate}/>)}
            {!!details.dueDate && (dueDateIsEditable ?
                <DocumentDataField id="dueDate" label={translate("documentData.dueDate")} warnings={warnings.dueDate}>
                    {renderDatePicker(DUE_DATE, toIsoDate(details.dueDate), "dueDate-datepicker")}
                </DocumentDataField> :
                <DocumentDataField id="dueDate" label={translate("documentData.dueDate")}
                                   value={formatDate(details.dueDate, null)} warnings={warnings.dueDate}/>)}
            {details.paymentPreApprovalDetails &&
                <PreApprovalDetails paymentDetails={details.paymentPreApprovalDetails} translate={translate}/>}
            {details.creditBankAccount &&
                <DocumentDataField id="creditBankAccount" label={translate("documentData.bankAccount")}
                                   value={details.creditBankAccount} warnings={warnings.creditBankAccount}/>}
            {details.amount !== null &&
                <DocumentAmountField details={details} warnings={warnings.amount} translate={translate}/>}
            {details.documentOrganizationUnit &&
                <DocumentDataField id="documentOrganizationUnit" label={translate("documentData.documentOrganizationUnit")}
                                   value={formatDocumentOrganizationUnit(details.documentOrganizationUnit)}
                                   warnings={warnings.documentOrganizationUnit}/>}
        </DocumentDataGrid>
    );
};

export default WorkflowDocumentDetailsDTO;
